import { faCalendar, faRedo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { checkReCaptcha, createEvent, getOffices, getServices, getServiceTimeSlots } from '../resources/api-constants'
import { ERRORS_MESSAGES, gReCaptchaSiteKey, mailRegex, ROUTES } from '../resources/other-constants'
import { Holiday, Office } from '../types/offices'
import { CreateEventData, CreatedEvent, EventSlots, Recurrence, Service, Slot } from '../types/services'
import CustomAxios from '../utility/customAxios'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import 'moment/locale/it'
import crypto from 'crypto'
import { useNavigate } from 'react-router'
import it from 'date-fns/locale/it'
import { addMonths } from 'date-fns'
import Loader from '../components/Loader'

enum UserType {
    PRIVATE = 1,
    COMPANY = 2,
    FREE_PROFESSIONAL = 3,
    PUBLIC_ENTITY = 4,
    ASSOCIATION = 5
}

const userTypes = [
    {
        label: 'Privato',
        value: UserType.PRIVATE
    },
    {
        label: 'Impresa',
        value: UserType.COMPANY
    },
    {
        label: 'Libero professionista',
        value: UserType.FREE_PROFESSIONAL
    },
    {
        label: 'Ente pubblico',
        value: UserType.PUBLIC_ENTITY
    },
    {
        label: 'Associazione di categoria/sindacale',
        value: UserType.ASSOCIATION
    }
]

const HomePage: React.FC = () => {
    const [publicUserEmailError, setPublicUserEmailError] = useState(false)
    const [publicUserPhoneError, setPublicUserPhoneError] = useState(false)
    const [datepickerIsOpen, setDatepickerIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingSendRequest, setLoadingSendRequest] = useState(false)
    const [loadingServices, setLoadingServices] = useState(false)
    const [loadingHeadquarters, setLoadingHeadquarters] = useState(false)
    const [userHasAcceptedPrivacyAndTerms, setUserHasAcceptedPrivacyAndTerms] = useState(false)
    const [userHasAcceptedWhatsapp, setUserHasAcceptedWhatsapp] = useState(false)
    const [userHasAcceptedMailingListTerms, setUserHasAcceptedMailingListTerms] = useState(false)
    const [userIsValid, setUserIsValid] = useState<boolean | null>(null)
    const [responseError, setResponseError] = useState('')
    const [publicUserName, setPublicUserName] = useState('')
    const [publicUserLastName, setPublicUserLastName] = useState('')
    const [publicUserEmail, setPublicUserEmail] = useState('')
    const [publicUserPhone, setPublicUserPhone] = useState('')
    const [selectedUserType, setSelectedUserType] = useState<UserType | null>(null)
    const [offices, setOffices] = useState<Office[]>([])
    const [headquarters, setHeadquarters] = useState<Office[]>([])
    const [availableServices, setAvailableServices] = useState<Service[]>([])
    const [availableTimeSlots, setAvailableTimeSlots] = useState<Slot[] | null>(null)
    const [availableTimeSlotMorning, setAvailableTimeSlotMorning] = useState<Slot[] | null>(null)
    const [availableTimeSlotAfternoon, setAvailableTimeSlotAfternoon] = useState<Slot[] | null>(null)
    const [displayedDate, setDisplayedDate] = useState('Scegli un giorno...')
    const [selectedDateToSend, setSelectedDateToSend] = useState<Date | null>(null)
    const [selectedHeadquarterId, setSelectedHeadquarterId] = useState(-1)
    const [selectedServiceId, setSelectedServiceId] = useState(-1)
    const [selectedServiceOfficeId, setSelectedServiceOfficeId] = useState(-1)
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('')
    const navigate = useNavigate()

    const mailingListTermsDocLink = 'https://tno.camcom.it/informative-privacy-servizio-informativo-tramite-mailup'
    const privacyTermsDocLink = 'https://tno.camcom.it/informativa-privacy-appuntamenti-telematici-agli-sportelli'

    /**
     * Genera un token statico dall'hash di una stringa.
     * @returns Hash token per autenticare le richieste alle API
     */
    const generateToken = (): string => {
        return crypto.createHash(`sha256`).update(`CLIENTCCPISA`).digest('hex')
    }

    /**
     * Dagli uffici filtra quelli che non sono sedi operative, che hanno come sede operativa di
     * riferimento quella selezionata, e li usa per ricavare tutti i servizi erogati.
     * I servizi disponibili saranno solo quelli collegati ad almento un operatore e con almeno
     * uno slot di ricorrenza.
     */
    const getAllServices = async (headquarterId: number): Promise<void> => {
        try {
            setLoadingServices(true)
            const services: Service[] = await CustomAxios.get(getServices(headquarterId)).then((response) => response.data.items)
            setAvailableServices(
                services.filter(
                    (service) =>
                        service.operators.length > 0 &&
                        service.slotAttributes &&
                        service.slotAttributes.recurrence &&
                        service.slotAttributes.recurrence.length > 0
                )
            )
            setLoadingServices(false)
        } catch (error) {
            console.error(error)
            setAvailableServices([])
            setLoadingServices(false)
        }
    }

    /**
     * Recupera tutte le sedi operative dal database
     */
    const getAllHeadquarters = async () => {
        try {
            setLoadingHeadquarters(true)
            const allOffices: Office[] = await CustomAxios.get(getOffices()).then((response) => response.data.items)
            setOffices(allOffices)
            setHeadquarters(allOffices.filter((office) => office.isHq && office.relationshipLevel && office.relationshipLevel > 1))
            setLoadingHeadquarters(false)
        } catch (error) {
            console.error(error)
            setLoadingHeadquarters(false)
        }
    }

    /**
     * Recupera i giorni della settimana in cui un servizio viene fornito. Restituisce un arrai di numeri da 0 a 6
     */
    useEffect(() => {
        const selectedService = availableServices.find((service: Service) => service.id === selectedServiceId)
        if (!selectedService) return
        setSelectedServiceOfficeId(selectedService.idOffice)
    }, [selectedServiceId, availableServices])

    /**
     * Controlla se una data presente o meno tra gli invalidSlots
     * @param invalidSlots slots tra cui controllare la presenza della data
     * @param date data da controllare
     */
    const checkInvalidSlots = useCallback((invalidSlots: Holiday[], date: string) => {
        const validDate = moment(date)
        const foundInvalidSlot = invalidSlots.find((item) => validDate.isSame(item.date, 'day'))
        return foundInvalidSlot
    }, [])

    /**
     * Controlla se una data è presente tra i giorni di chiusura di un ufficio
     * o tra quelli della sua sede
     * @param office ufficio del servizio su cui eseguire il controllo
     * @param date data da controllare
     * @author Filippo Busi
     */
    const checkOfficeInvalidSlots = useCallback(
        (office: Office, date: string) => {
            const hq = office.hq
            const office_invalid_slot = office.invalidSlots
            if (office_invalid_slot && office_invalid_slot.length > 0) {
                if (checkInvalidSlots(office_invalid_slot, date)) {
                    return false
                }
            }
            if (hq && hq.invalidSlots && hq.invalidSlots.length > 0) {
                if (checkInvalidSlots(hq.invalidSlots, date)) {
                    return false
                }
            }
            return true
        },
        [checkInvalidSlots]
    )

    /**
     * Controlla se una data è selezionabile dall'utente all'interno del componente del calendario.
     * I giorni disponibili saranno quelli in cui un servizio viene erogato.
     * @param size numero massimo di date disponibili che si desidera ottenere
     * @param eventSlots
     * @param office ufficio del servizio selezionato da cui recuperare gli invalidSlots
     * @author Filippo Busi
     */
    const getDays = useCallback(
        (size: number, days: number[], eventSlots: EventSlots[], office: Office) => {
            const outDays = []
            let i = 0
            while (outDays.length < size) {
                let day: any
                if (i == 0) {
                    day = moment()
                } else {
                    day = moment().add(i, 'days')
                }
                // Controlla la chiusura degli uffici
                if (checkOfficeInvalidSlots(office, day.format())) {
                    // Se l'indice del giorno è nell'array dei giorni disponibili allora controlla gli event_slots
                    // Specifico undefined perchè lunedì corrisponde a zero
                    if (days.find((d) => d == day.weekday()) !== undefined) {
                        if (eventSlots && eventSlots.length > 0) {
                            // Filtra solo per gli slot che disabilitano tutto il giorno
                            const filteredSlots = eventSlots.filter((item) => !item.active && !item.timeStart && moment(item.date).isSame(day, 'day'))
                            if (filteredSlots.length <= 0) {
                                outDays.push(day.format())
                            }
                        } else {
                            // Se non ci sono event_slots allora inserisce il giorno
                            outDays.push(day.format())
                        }
                    } else {
                        if (eventSlots && eventSlots.length > 0) {
                            // Controlla che non sia un giorno aggiunto da event_slots
                            const filteredSlots = eventSlots.filter((item) => item.active && moment(item.date).isSame(day, 'day'))
                            if (filteredSlots.length > 0) {
                                outDays.push(day.format())
                            }
                        }
                    }
                }
                i++
            }
            return outDays.map((day) => new Date(day))
        },
        [checkOfficeInvalidSlots]
    )

    /**
     * Restituisce le date disponibili dalle recurrence di un servizio
     * @param recurrence ricorrenze di erogazione di un servizio
     * @author Filippo Busi
     */
    const extrapolateCalendarDates = useCallback((recurrence: Recurrence[]) => {
        let availableDays: number[] = []
        recurrence.forEach((r) => {
            availableDays = Array.from(new Set([...availableDays, ...r.days]))
        })
        return availableDays
    }, [])

    /**
     * Restituisce i giorni di erogazione del servizio selezionato
     */
    const getServiceValidDates = useMemo(() => {
        const selectedServiceObj = availableServices.find((serv) => serv.id === selectedServiceId)
        const selectedOfficeObj = offices.find((off) => off.id === selectedServiceOfficeId)
        if (!selectedServiceObj || !selectedOfficeObj) return undefined

        // Restituisce un massimo di 60 giorni validi per la prenotazione di un servizio
        return getDays(60, extrapolateCalendarDates(selectedServiceObj.slotAttributes.recurrence), selectedServiceObj.eventSlots, selectedOfficeObj)
    }, [availableServices, getDays, extrapolateCalendarDates, selectedServiceId, selectedServiceOfficeId, offices])

    /**
     * Controlla che l'orario di uno slot sia maggiore o uguale (futuro o presente)
     * rispetto all'orario corrente.
     * @param sHours ore dello slot
     * @param cHours ora corrente
     * @param sMinutes minuti dello slot
     * @param cMinutes minuti correnti
     * @returns flag che indica che l'orario è maggiore o uguale a quello corrente
     */
    const checkTimeYetToCome = (sHours: number, cHours: number, sMinutes: number, cMinutes: number) => {
        if (sHours === cHours) {
            return sMinutes >= cMinutes
        }
        return sHours > cHours
    }

    /**
     * Controlla che ore e minuti di uno slot siano più
     * in avanti temporalmente rispetto a quelle attuali.
     * @returns Flag che indica se lo slot è già passato o meno.
     */
    const slotIsYetToStart = useCallback(
        (slot: Slot): boolean => {
            // controlla se abbiamo selezionato un giorno nel futuro (es. domani, dopodomani,...)
            if (moment().startOf('day').diff(moment(selectedDateToSend), 'days') < 0) {
                return true
            }
            // altrimenti compara gli orari
            const [currentHours, currentMinutes] = moment().format('HH:mm').split(':')
            const [slotHours, slotMinutes] = slot.start.split(':')
            const yetToCome = checkTimeYetToCome(parseInt(slotHours, 10), parseInt(currentHours, 10), parseInt(slotMinutes, 10), parseInt(currentMinutes, 10))
            return yetToCome
        },
        [selectedDateToSend]
    )

    /**
     * Ricava gli orari disponibili per la data e il servizio selezionati e li divide in due
     * gruppi: mattina e pomeriggio. Gli slot di orari validi sono quelli con operatori disponibili
     * e con ora d'inizio temporalmente maggiore rispetto a quella corrente.
     */
    const getAvailableTimeSlots = useCallback(async (): Promise<void> => {
        if (selectedServiceId !== -1 && selectedDateToSend !== null) {
            try {
                setLoading(true)
                const parsedUtcDate = moment(selectedDateToSend).format('YYYY-MM-DD')
                const startDate: string = parsedUtcDate.concat('T00:00:00Z')
                const endDate: string = parsedUtcDate.concat('T23:59:59Z')
                const serviceTimeSlots = await CustomAxios.get(getServiceTimeSlots(selectedServiceId, startDate, endDate)).then((response) => response.data[0])
                const filteredSlots = serviceTimeSlots
                    ? serviceTimeSlots.slots.filter((slot: any) => slot.availableOperators.length > 0 && slotIsYetToStart(slot))
                    : []
                const morningSlots: Slot[] = []
                const afternoonSlots: Slot[] = []
                filteredSlots?.map((slot: any) => {
                    const startTime = parseInt(slot.start.split(':')[0], 10)
                    if (startTime >= 0 && startTime < 13) {
                        morningSlots.push(slot)
                    } else {
                        afternoonSlots.push(slot)
                    }
                })
                setAvailableTimeSlots(filteredSlots)
                setAvailableTimeSlotMorning(morningSlots)
                setAvailableTimeSlotAfternoon(afternoonSlots)
                setLoading(false)
            } catch (error) {
                console.error(error)
                setLoading(false)
            }
        } else {
            setSelectedTimeSlot('')
            setAvailableTimeSlots([])
            setAvailableTimeSlotMorning([])
            setAvailableTimeSlotAfternoon([])
        }
    }, [selectedServiceId, selectedDateToSend, slotIsYetToStart])

    /**
     * Imposta la data selezionata come valore della rispettiva state
     * @param newDate Data selezionata dall'utente
     */
    const changeSelectedDate = useCallback((newDate: Date) => {
        const formattedDateString = moment(newDate).locale('it').format('D MMMM YYYY')
        setDisplayedDate(formattedDateString)
        setSelectedDateToSend(newDate)
        setDatepickerIsOpen(false)
    }, [])

    /**
     * Ogni minuto recupera gli slot temporali disponibili aggiornati.
     */
    useEffect(() => {
        const mainInterval = setInterval(() => {
            getAvailableTimeSlots()
        }, (60 - moment().seconds()) * 1000)
        return () => {
            clearInterval(mainInterval)
        }
    }, [getAvailableTimeSlots])

    /**
     * Al primo render della pagina genera il token per le chiamate API, lo assegna
     * ad Axios e recupera tutte le sedi operative disponibili.
     */
    useEffect(() => {
        let token = generateToken()
        CustomAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        getAllHeadquarters()
        return () => {
            token = ''
        }
    }, [])

    /**
     * Aggiorna i servizi visibili e quello selezionato in base alla sede operativa selezionata.
     */
    useEffect(() => {
        setSelectedServiceId(-1)
        if (selectedHeadquarterId !== -1) {
            getAllServices(selectedHeadquarterId)
        }
        return () => {
            setSelectedServiceId(-1)
        }
    }, [selectedHeadquarterId])

    /**
     * Controlla che la mail inserita rispetti un formato valido
     */
    const checkIsValidEmail = useCallback(() => {
        if (publicUserEmail.length > 0) {
            setPublicUserEmailError(!mailRegex.test(publicUserEmail))
        }
        if (publicUserEmail.length === 0) {
            setPublicUserEmailError(false)
        }
    }, [publicUserEmail])

    /**
     * Controlla che il telefono inserito rispetti un formato valido (solo cifre, 8-11 caratteri).
     */
    const checkIsValidPhone = useCallback(() => {
        const phoneRegex = /^[0-9]{8,11}$/
        if (publicUserPhone.length > 0) {
            setPublicUserPhoneError(!phoneRegex.test(publicUserPhone))
        }
        if (publicUserPhone.length === 0) {
            setPublicUserPhoneError(false)
        }
    }, [publicUserPhone])

    /**
     * Quando il servizio o la data selezionati cambiano, gli slot vengono aggiornati.
     */
    useEffect(() => {
        setAvailableTimeSlots([])
        setAvailableTimeSlotMorning([])
        setAvailableTimeSlotAfternoon([])
        setSelectedTimeSlot('')
        getAvailableTimeSlots()
    }, [selectedServiceId, getAvailableTimeSlots, selectedDateToSend])

    /**
     * Attende 500 ms da quando l'utente ha smesso di scrivere nell'input per la mail
     * e controlla se la stringa inserita è un indirizzo valido.
     */
    useEffect(() => {
        const timeOut = setTimeout(() => checkIsValidEmail(), 500)
        return () => {
            clearTimeout(timeOut)
        }
    }, [publicUserEmail, checkIsValidEmail])

    /**
     * Attende 500 ms da quando l'utente ha smesso di scrivere nell'input per il telefono
     * e controlla se la stringa inserita è un numero valido.
     */
    useEffect(() => {
        const timeOut = setTimeout(() => checkIsValidPhone(), 500)
        return () => {
            clearTimeout(timeOut)
        }
    }, [publicUserPhone, checkIsValidPhone])

    /**
     * Controlla la validità del nome e cognome dell'utente che prenota
     */
    const publicUserDataIsValid = useMemo((): boolean => {
        return publicUserLastName !== '' && publicUserName !== ''
    }, [publicUserLastName, publicUserName])

    /**
     * Imposta il messaggio di errore a seconda della risposta delle API
     * @param response Risposta delle API in caso di errore
     */
    const getErrorFromResponse = (response: any) => {
        const responseMessage: string = response.message
        let messageToDisplay = ''
        switch (responseMessage) {
            case ERRORS_MESSAGES.OPERATOR_NOT_AVAILABLE:
                messageToDisplay = 'Siamo dispiaciuti, lo slot selezionato non è più disponibile.'
                break
            case ERRORS_MESSAGES.SLOT_ALREADY_TAKEN_FROM_USER:
                messageToDisplay =
                    'Hai già prenotato un appuntamento per questo servizio al giorno selezionato. Ti ricordiamo che, per lo stesso servizio, è possibile prenotare un solo appuntamento al giorno.'
                break
            case ERRORS_MESSAGES.TOKEN_INVALID:
                messageToDisplay = 'Si è verificato un errore. Per favore, riprova.'
                break
            case ERRORS_MESSAGES.RECAPTCHA_ERROR:
                messageToDisplay = 'Google reCAPTCHA ha segnalato un errore.'
                break
            case ERRORS_MESSAGES.MAIL_FORMAT_NOT_VALID:
                messageToDisplay = "L'indirizzo email inserito non risulta valido. Per favore inserisci un indirizzo corretto."
                break
            default:
                messageToDisplay = 'Si è verificato un errore durante il processo di prenotazione. Per favore, ricontrolla i dati inseriti e riprova.'
                break
        }
        setResponseError(messageToDisplay)
    }

    /**
     * Invia i dati alla chiamata del server connessa a quella di Google: ``https://www.google.com/recaptcha/api/siteverify``
     * ed elabora la risposta per capire la validità delle interazioni dell'utente sulla pagina.
     * @param token token ricavato dal servizio di Google reCAPTCHA v3
     * @returns flag che indica la validità della risposta ricevuta
     */
    const submitTokenData = async (token: any): Promise<boolean> => {
        const averageScore = 0.5
        try {
            const GResponse = await CustomAxios.post(checkReCaptcha(), { token: token }).then((response) => response.data)
            return GResponse !== null && GResponse.success && GResponse.score.toFixed(1) >= averageScore.toFixed(1)
        } catch (error) {
            console.error(error)
            return false
        }
    }

    /**
     * Sfrutta il servizio di Google ``reCAPTCHA v3`` per verificare se l'utente che interagisce con la pagina e che
     * compie l'azione è un bot o una persona. L'azione è definita come
     * 'createEventOnLocalhost', se siamo in development,  o 'createEvent' se siamo in produzione.
     */
    const checkGcaptchaValidation = async () => {
        setLoadingSendRequest(true)
        const performedActionName: string = !process.env.NODE_ENV || process.env.NODE_ENV === 'development' ? 'createEventOnLocalhost' : 'createEvent'
        // eslint-disable-next-line
        ;(window as any).grecaptcha.ready(() => {
            // eslint-disable-next-line
            ;(window as any).grecaptcha.execute(gReCaptchaSiteKey, { action: performedActionName }).then(async (token: any) => {
                setUserIsValid(await submitTokenData(token).then((isValid) => isValid))
            })
        })
    }

    /**
     * Invia i dati necessari per generare la prenotazione sul calendario della sede operativa.
     * In caso di successo esegue un redirect alla pagina di recap con i dati restituiti dall'API
     * per la creazione dell'evento.
     */
    const sendEventData = useCallback(async () => {
        try {
            setResponseError('')
            if (userIsValid) {
                const formattedDate = moment(selectedDateToSend).format('YYYY-MM-DD')
                const selectedOperator = availableTimeSlots?.find((slot) => slot.start === selectedTimeSlot)?.availableOperators[0].id
                const permission: string[] = []
                if (userHasAcceptedWhatsapp) permission.push('WA')
                if (mailingListTermsDocLink) permission.push('MU')
                const data: CreateEventData = {
                    reservation: {
                        date: formattedDate,
                        time: selectedTimeSlot,
                        id_operator: selectedOperator || -1,
                        id_service: selectedServiceId,
                    },
                    customer: {
                        lastName: publicUserLastName,
                        firstName: publicUserName,
                        phone: publicUserPhone,
                        email: publicUserEmail,
                        userType: selectedUserType !== null ? parseInt(selectedUserType.toString()) : 0,
                        permission
                    }
                }
                const createdEvent: CreatedEvent = await CustomAxios.post(createEvent(), data).then((response) => response.data)
                navigate(ROUTES.RECAPPAGE_ROUTE, { state: { eventData: createdEvent } })
                setResponseError('')
                setLoadingSendRequest(false)
            } else {
                throw {
                    response: {
                        name: 'reCaptchaException',
                        success: userIsValid,
                        data: {
                            message: 'reCaptchaValidationError'
                        }
                    }
                }
            }
        } catch (error: any) {
            setLoadingSendRequest(false)
            setUserIsValid(null)
            console.error(error)
            if (error && error.response) {
                if (error.response.status === 409) {
                    setResponseError('Hai già una prenotazione attiva per questo servizio nella data selezionata.')
                    return
                }
                getErrorFromResponse(error.response.data)
            }
        }
    }, [
        selectedUserType,
        userIsValid,
        availableTimeSlots,
        navigate,
        publicUserEmail,
        publicUserLastName,
        publicUserName,
        publicUserPhone,
        userHasAcceptedWhatsapp,
        selectedDateToSend,
        selectedServiceId,
        selectedTimeSlot
    ])

    /**
     * Funzione che chiama sendEventData() solo quando userIsValid cambia stato.
     * Non lancia nulla al primo render
     */
    useEffect(() => {
        if (userIsValid !== null) {
            sendEventData()
        }
        return () => {
            setUserIsValid(null)
        }
    }, [userIsValid, sendEventData])

    /**
     * Controlla se uno slot temporale è stato selezionato dall'utente
     * @param value Parametro "start" dello slot da esaminare
     * @returns Flag che indica se lo slot in esame è quello selezionato
     */
    const checkIsSelectedTimeSlot = (value: string): boolean => {
        return selectedTimeSlot === value
    }

    /**
     * Controlla che ogni campo necessario sia stato compilato nella maniera corretta
     */
    const checkAllDataIsCorrect = useMemo((): boolean => {
        const phoneRegex = /^[0-9]{8,11}$/
        return (
            publicUserDataIsValid &&
            !publicUserEmailError &&
            publicUserEmail.length > 8 &&
            mailRegex.test(publicUserEmail) &&
            selectedServiceId !== -1 &&
            selectedDateToSend !== null &&
            selectedTimeSlot !== '' &&
            !publicUserPhoneError &&
            publicUserPhone.length >= 8 &&
            phoneRegex.test(publicUserPhone) &&
            userHasAcceptedPrivacyAndTerms &&
            selectedUserType !== null
        )
    }, [
        publicUserDataIsValid,
        publicUserEmailError,
        publicUserEmail,
        selectedServiceId,
        selectedDateToSend,
        selectedTimeSlot,
        publicUserPhoneError,
        publicUserPhone,
        userHasAcceptedPrivacyAndTerms,
        selectedUserType
    ])

    /**
     * Apre/Chiude il componente per la selezione della data
     */
    const handleClickDisplayedDate = () => {
        if (selectedServiceId === -1) return
        setDatepickerIsOpen(!datepickerIsOpen)
    }

    const displayMorningTimeSlots = () => {
        if (availableTimeSlotMorning !== null && availableTimeSlotMorning.length > 0) {
            return (
                <>
                    <span className="time-slots-group-indicator">Mattina</span>
                    <div className="time-slots-container">
                        {availableTimeSlotMorning.map((timeSlot: Slot, index: number) => {
                            return (
                                <div
                                    key={index}
                                    className={`time-slot ${checkIsSelectedTimeSlot(timeSlot.start) ? 'selected-time-slot' : ''}`}
                                    onClick={() => setSelectedTimeSlot(timeSlot.start)}
                                >
                                    {timeSlot.start}
                                </div>
                            )
                        })}
                    </div>
                </>
            )
        }
        return ''
    }

    const displayAfternoonTimeSlots = () => {
        if (availableTimeSlotAfternoon !== null && availableTimeSlotAfternoon.length > 0) {
            return (
                <>
                    <span className="time-slots-group-indicator">Pomeriggio</span>
                    <div className="time-slots-container">
                        {availableTimeSlotAfternoon.map((timeSlot: Slot, index: number) => {
                            return (
                                <div
                                    key={index}
                                    className={`time-slot ${checkIsSelectedTimeSlot(timeSlot.start) ? 'selected-time-slot' : ''}`}
                                    onClick={() => setSelectedTimeSlot(timeSlot.start)}
                                >
                                    {timeSlot.start}
                                </div>
                            )
                        })}
                    </div>
                </>
            )
        }
        return ''
    }

    return (
        <>
            {loadingSendRequest && <Loader />}
            <div className="page-content homepage-page-content">
                <p className="subtitle">Benvenuto nel servizio di prenotazione online della Camera di Commercio Toscana Nord-Ovest.</p>
                <p className="subtitle bottom-space">
                    Inserisci i tuoi dati compilando <b>tutti</b> i campi e riceverai una mail di <b>riepilogo della prenotazione</b> all’indirizzo che hai
                    inserito.
                </p>
                <div className="form">
                    <div className="input-row">
                        <input type="text" className="input" placeholder="Nome" value={publicUserName} onChange={(e) => setPublicUserName(e.target.value)} />
                        <input
                            type="text"
                            className="input"
                            placeholder="Cognome"
                            value={publicUserLastName}
                            onChange={(e) => setPublicUserLastName(e.target.value)}
                        />
                    </div>
                    <div className="input-row">
                        <div className="input-with-helper">
                            <input
                                type="text"
                                className={`input ${publicUserEmailError ? 'input-error' : ''}`}
                                placeholder="Email"
                                value={publicUserEmail}
                                onChange={(e) => setPublicUserEmail(e.target.value)}
                            />
                            {publicUserEmailError && <span className="input-helper-text">Inserisci un indirizzo email valido.</span>}
                        </div>
                        <div className="input-with-helper">
                            <input
                                type="text"
                                maxLength={11}
                                className={`input ${publicUserPhoneError ? 'input-error' : ''}`}
                                placeholder="Telefono"
                                value={publicUserPhone}
                                onChange={(e) => {
                                    if (/^[0-9]+$/.test(e.target.value) || e.target.value === '') {
                                        setPublicUserPhone(e.target.value)
                                    }
                                }}
                            />
                            {publicUserPhoneError && <span className="input-helper-text">Inserisci un numero valido (solo cifre, 8–11 caratteri).</span>}
                        </div>
                    </div>
                    <div className="input-row">
                        <select className="input" onChange={(e) => setSelectedUserType(e.target.value as unknown as UserType)}>
                            <option value={-1}>Scegli il tipo di utente</option>
                            {userTypes.map((userType: { label: string; value: UserType }) => {
                                return (
                                    <option key={userType.value} value={userType.value}>
                                        {userType.label}
                                    </option>
                                )
                            })}
                        </select>
                        <div className="input hidden-input-element" style={{ opacity: 0 }} />
                    </div>
                    <div className="checkboxes-container">
                        <div className="input-row">
                            <div>
                                <input
                                    type="checkbox"
                                    id="privacyAndTermsAccept"
                                    name="privacyAndTermsAccept"
                                    checked={userHasAcceptedPrivacyAndTerms}
                                    onChange={() => setUserHasAcceptedPrivacyAndTerms(!userHasAcceptedPrivacyAndTerms)}
                                />
                                <label html-for="privacyAndTermsAccept">
                                    Accetto la
                                    <a className="margin-link" href={privacyTermsDocLink} rel="noreferrer" target="_blank">
                                        Privacy Policy e i Termini di trattamento dei dati
                                    </a>
                                    previsti dal servizio.
                                </label>
                            </div>
                        </div>
                        <div className="input-row">
                            <div>
                                <input
                                    type="checkbox"
                                    id="whatsappAccept"
                                    name="whatsappAccept"
                                    checked={userHasAcceptedWhatsapp}
                                    onChange={() => setUserHasAcceptedWhatsapp(!userHasAcceptedWhatsapp)}
                                />
                                <label html-for="whatsappAccept">
                                    Autorizzo la camera di commercio a contattarmi, tramite numero fornito, su piattaforma WhatsApp per tenermi informato su
                                    eventi futuri.
                                </label>
                            </div>
                        </div>
                        <div className="input-row">
                            <div>
                                <input
                                    type="checkbox"
                                    id="mailingListAccept"
                                    name="mailingListAccept"
                                    checked={userHasAcceptedMailingListTerms}
                                    onChange={() => setUserHasAcceptedMailingListTerms(!userHasAcceptedMailingListTerms)}
                                />
                                <label html-for="mailingListAccept">
                                    Autorizzo l&apos;inserimento del mio indirizzo email nella mailing list della camera di commercio. Mediante modalità
                                    descritta
                                    <a className="margin-link" href={mailingListTermsDocLink} rel="noreferrer" target="_blank">
                                        nell&apos;informativa per l&apos;utilizzo del servizio informativo tramite Mailup
                                    </a>
                                    .
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                {publicUserDataIsValid && (
                    <>
                        <div className="horizontal-divider" />
                        <p className="subtitle">Seleziona la sede operativa presso cui vuoi prenotare, un servizio e un giorno.</p>
                        <div className="second-form">
                            <div className="input-row">
                                <div className={`select-container ${loadingHeadquarters ? 'select-is-loading' : ''}`}>
                                    <select
                                        disabled={loadingHeadquarters}
                                        className="input"
                                        onChange={(e) => setSelectedHeadquarterId(parseInt(e.target.value))}
                                    >
                                        <option value={-1}>Scegli una sede operativa</option>
                                        {headquarters.map((headquarter: Office) => {
                                            return (
                                                <option key={headquarter.id} value={headquarter.id}>
                                                    {headquarter.name}
                                                </option>
                                            )
                                        })}
                                    </select>
                                    {loadingHeadquarters && (
                                        <div className="reload-icon">
                                            <FontAwesomeIcon icon={faRedo} />
                                        </div>
                                    )}
                                </div>
                                <div className={`select-container ${loadingServices ? 'select-is-loading' : ''}`}>
                                    <select
                                        disabled={loadingServices || selectedHeadquarterId === -1}
                                        className="input"
                                        defaultValue={40}
                                        onChange={(e) => setSelectedServiceId(parseInt(e.target.value))}
                                    >
                                        <option value={-1}>Scegli un servizio</option>
                                        {availableServices.map((service: Service) => {
                                            return (
                                                <option key={service.id} value={service.id}>
                                                    {service.name}
                                                </option>
                                            )
                                        })}
                                    </select>
                                    {loadingServices && (
                                        <div className="reload-icon">
                                            <FontAwesomeIcon icon={faRedo} />
                                        </div>
                                    )}
                                </div>
                                <div className={`input input-with-left-icon ${selectedServiceId === -1 ? 'disabled-selector' : ''}`}>
                                    <div className="icon">
                                        <FontAwesomeIcon icon={faCalendar} />
                                    </div>
                                    <label className="label-on-top" onClick={() => handleClickDisplayedDate()}>
                                        {displayedDate}
                                    </label>
                                    <div style={{ position: 'absolute', width: '90%' }}>
                                        <DatePicker
                                            minDate={moment().toDate()}
                                            maxDate={addMonths(new Date(), 2)}
                                            locale={it}
                                            includeDates={getServiceValidDates}
                                            open={datepickerIsOpen}
                                            selected={selectedDateToSend}
                                            onChange={(date: Date) => changeSelectedDate(date)}
                                            onSelect={(date: Date) => changeSelectedDate(date)}
                                            placeholderText="Scegli un giorno..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {selectedDateToSend !== null && availableTimeSlots !== null && (
                            <>
                                <div className="horizontal-divider" />
                                <p className="subtitle">Seleziona, tra gli orari disponibili nel giorno selezionato, quello che preferisci.</p>
                                {availableTimeSlots.length > 0 ? (
                                    <div className="timeSlotsPanel">
                                        {displayMorningTimeSlots()}
                                        {displayAfternoonTimeSlots()}
                                    </div>
                                ) : (
                                    <div className="empty-box">
                                        {loading ? (
                                            <p className="empty-box-message">Sto caricando gli orari disponibili ...</p>
                                        ) : (
                                            <p className="empty-box-message">Al momento non vi sono orari disponibili per il giorno selezionato.</p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
                <div className="medium-margin-top-container">
                    {responseError !== '' && (
                        <p
                            style={{ color: '#ff3232', fontWeight: 'bold', fontSize: '18px', padding: '10px', backgroundColor: 'rgba(255, 50, 50, .1)' }}
                            className="subtitle"
                        >
                            {responseError}
                        </p>
                    )}
                    <button className="primary-button" disabled={!checkAllDataIsCorrect || loadingSendRequest} onClick={() => checkGcaptchaValidation()}>
                        Conferma
                    </button>
                </div>
            </div>
            {/** <Footer /> */}
        </>
    )
}

export default HomePage
